import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { webApi, influencerApi } from '@/lib/api';
import { useNewInfluencerAuth } from '@/lib/config/apiFlags';

/**
 * 원본: .references/gc-mono/apps/gc-zoku/redux/modules/envs.js
 * 환경 설정 및 현재 사용자 정보
 */

export interface CurrentUser {
  _id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  channels?: Array<{
    channelType: string;
    channelId: string;
    channelName: string;
  }>;
  [key: string]: unknown;
}

export interface Admin {
  id: string;
  title?: string;
  avatar?: string;
  [key: string]: unknown;
}

interface EnvsState {
  currentUser: CurrentUser | null;
  env: Record<string, unknown> | null;
  advertisingTypes: unknown[] | null;
  socialmediaTypes: unknown[] | null;
  admins: Admin[];
  loading: boolean;
}

const initialState: EnvsState = {
  currentUser: null,
  env: null,
  advertisingTypes: null,
  socialmediaTypes: null,
  admins: [],
  loading: false,
};

export const fetchEnvs = createAsyncThunk(
  'envs/get',
  async () => useNewInfluencerAuth()
    ? influencerApi.post<{ currentUser: CurrentUser; env: Record<string, unknown>; admins?: Admin[] }>('envs/get')
    : webApi.post<{ currentUser: CurrentUser; env: Record<string, unknown>; admins?: Admin[] }>('envs/get'),
);

export const fetchAdvertisingTypes = createAsyncThunk(
  'envs/advertisingTypes',
  async () => useNewInfluencerAuth()
    ? influencerApi.get<unknown[]>('envs/types_of_advrtsng/get')
    : webApi.get<unknown[]>('envs/types_of_advrtsng/get'),
);

export const fetchSocialmediaTypes = createAsyncThunk(
  'envs/socialmediaTypes',
  async () => useNewInfluencerAuth()
    ? influencerApi.get<unknown[]>('envs/social-media-types/get')
    : webApi.get<unknown[]>('envs/socialmedia_types/get'),
);

const envsSlice = createSlice({
  name: 'envs',
  initialState,
  reducers: {
    clearUser(state) {
      state.currentUser = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEnvs.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchEnvs.fulfilled, (state, action) => {
        state.loading = false;
        state.currentUser = action.payload.currentUser;
        state.env = action.payload.env;
        if (action.payload.admins) state.admins = action.payload.admins;
      })
      .addCase(fetchEnvs.rejected, (state) => {
        state.loading = false;
        // API 실패 시에도 env를 빈 객체로 설정하여 ProtectedRoute가
        // "로딩 중" 상태에 머물지 않고 리다이렉트를 수행하도록 한다
        if (!state.env) state.env = {};
      })
      .addCase(fetchAdvertisingTypes.fulfilled, (state, action) => {
        state.advertisingTypes = action.payload;
      })
      .addCase(fetchSocialmediaTypes.fulfilled, (state, action) => {
        state.socialmediaTypes = action.payload;
      });
  },
});

export const { clearUser } = envsSlice.actions;
export default envsSlice.reducer;
